import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useJobs } from "../../hooks/useJobs";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useTeamRoster } from "../../hooks/useTeamRoster";
import type { NurseFullRole, TeamNurse } from "../../utils/models";
import type { HolidayGroup, HolidayPerson } from "./Holidays/holidayApi";
import { HOLIDAY_NONE } from "./Holidays/holidayFormat";
import { useHolidayGroups, useHolidayPeopleByIds } from "./Holidays/holidayQueries";
import { useOpenStatusDecisions } from "./statusDecisions/hooks";
import type { OpenStatusDecision } from "./statusDecisions/types";
import type { ActiveStatFilter, StatFilterKey } from "./TeamStats";
import {
	computeTeamFacetCounts,
	DEFAULT_SELECTED_STATUSES,
	filterTeamNurses,
	NO_JOB_FILTER_ID,
	type TeamFilterContext,
	type TeamFilterState,
} from "./teamFacetFilters";
import { clearTeamUrlState, countActiveTeamFilters, nextStatFilter, parseTeamUrlState, type TeamUrlState, writeTeamUrlState } from "./teamUrlState";
import { type TeamCardStats, useTeamCardStats } from "./useTeamCardStats";

export { DEFAULT_SELECTED_STATUSES };

/** Stable empty selection — used to *drop* a facet from a derived roster without a per-render `new Set()`. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set();

/** The holiday side of the roster: the department's groups and each person's group and day part. */
export type TeamHolidayData = {
	/** Undefined until the groups load, and when the department has none. */
	groups: HolidayGroup[] | undefined;
	/** The department has no holiday groups yet: the columns, filters and bulk action stay hidden. */
	notSetUp: boolean;
	/** Null until every person has loaded. */
	byNurseId: ReadonlyMap<number, HolidayPerson> | null;
	isLoading: boolean;
	isError: boolean;
	selectedGroups: Set<string>;
	selectedDayParts: Set<string>;
	onGroupsChange: (groups: Set<string>) => void;
	onDayPartsChange: (dayParts: Set<string>) => void;
	/** A holiday facet is on but the holiday data couldn't load: the table says so instead of "no matching staff". */
	filterError: boolean;
	/** Clears both holiday facets at once — the way out of `filterError`. */
	clearFilters: () => void;
};

export type UseTeamFiltersResult = {
	// Selected filter state.
	searchQuery: string;
	selectedRoles: NurseFullRole[] | undefined;
	selectedJobNames: string[];
	selectedSections: Set<string>;
	selectedStatuses: Set<string>;
	selectedIntegrators: Set<string>;
	selectedAgencies: Set<string>;
	selectedUnions: Set<string>;
	selectedLastLogin: Set<string>;
	selectedEmail: Set<string>;
	selectedPhone: Set<string>;
	selectedAutoApprove: Set<string>;
	showOnlyDecisions: boolean;
	activeStatFilter: ActiveStatFilter;

	// Change handlers.
	onSearchChange: (value: string) => void;
	onRolesChange: (roles: NurseFullRole[]) => void;
	onJobsChange: (jobNames: string[]) => void;
	onSectionsChange: (sections: Set<string>) => void;
	onStatusesChange: (statuses: Set<string>) => void;
	onAgenciesChange: (agencies: Set<string>) => void;
	onUnionsChange: (unions: Set<string>) => void;
	onLastLoginChange: (buckets: Set<string>) => void;
	onShowOnlyDecisionsChange: (value: boolean) => void;
	cycleStatFilter: (key: StatFilterKey) => void;
	clearStatFilter: () => void;
	clearAllFilters: () => void;
	handleViewAndDecide: () => void;

	// Derived values.
	filterState: TeamFilterState;
	nurses: TeamNurse[];
	/** Sidebar-filtered roster minus the web-only stat-card filter — used by mobile. */
	nursesIgnoringStatFilter: TeamNurse[];
	facetCounts: ReturnType<typeof computeTeamFacetCounts>;
	activeFilterCount: number;
	hasFiltersApplied: boolean;
	/** The whole department roster, for the "N of M" record count beside search. */
	totalNurseCount: number;
	/** The roster is still arriving: the stat tiles and the table render skeletons. */
	rosterLoading: boolean;
	/** The roster couldn't be loaded: the table shows its load-failed state instead of "no staff". */
	rosterError: boolean;

	holiday: TeamHolidayData;

	// Stats + decisions (owned here so the page doesn't recompute them).
	teamStats: TeamCardStats;
	decisionsByNurseId: Map<number, OpenStatusDecision>;
	decisionsCount: number;
	/** The roster is empty only because the data it filters against is still loading — render skeletons, not an empty state. */
	rosterPending: boolean;
};

/**
 * Owns every Team-page filter concern: the selected facets (held in the URL — see `teamUrlState.ts`),
 * all change handlers, and the derived table rows + facet counts + active-count. Also owns
 * `useTeamCardStats` and the open-status-decisions map so the page renders thin.
 */
export function useTeamFilters(): UseTeamFiltersResult {
	const { chosenDepartment } = useChosenDepartment();
	// The Auto-approval eligibility facet is gated behind this flag — a stale URL parameter only
	// filters when the flag is on, mirroring the old toolbar toggle.
	const autoApproveEnabled = useFeatureFlag(FEATURE_FLAGS.AutoApproveShifts);
	const enabledJobs = useJobs();
	const enabledJobNames = useMemo(() => new Set(enabledJobs.map((job) => job.name)), [enabledJobs]);
	const isSelectableJobName = useCallback((jobName: string) => jobName === NO_JOB_FILTER_ID || enabledJobNames.has(jobName), [enabledJobNames]);
	const departmentId = chosenDepartment.department_id;
	const { roster, isLoading: rosterLoading, isError: rosterError } = useTeamRoster(departmentId);
	const { trackError } = useAnalytics();
	useEffect(() => {
		if (rosterError) trackError("team_roster_fetch_error", { department_id: departmentId });
	}, [rosterError, departmentId, trackError]);

	// Everyone on the roster, deactivated people included: the holiday columns and facets cover every row.
	// Read only: opening Team must not set up holiday groups. Until the Holidays tab is opened there are none.
	const holidayGroupsQuery = useHolidayGroups(departmentId);
	const holidayNotSetUp = holidayGroupsQuery.data?.length === 0;
	const holidayGroups = holidayNotSetUp ? undefined : holidayGroupsQuery.data;
	const rosterIds = useMemo(() => (holidayGroups === undefined ? [] : roster.map((nurse) => nurse.id)), [holidayGroups, roster]);
	const holidayPeople = useHolidayPeopleByIds(departmentId, rosterIds);
	// Null until the groups and every person have loaded: an empty lookup made before the groups arrive isn't an answer.
	const holidayByNurseId = holidayGroups === undefined ? null : (holidayPeople.data ?? null);
	const holidayError = holidayGroupsQuery.isError || holidayPeople.isError;
	useEffect(() => {
		if (holidayError) trackError("team_holiday_people_fetch_error", { department_id: departmentId });
	}, [holidayError, departmentId, trackError]);

	const [searchParams, setSearchParams] = useSearchParams();
	const urlState = useMemo<TeamUrlState>(() => parseTeamUrlState(searchParams), [searchParams]);

	// Frozen at mount so the last-login buckets (and their counts) don't silently re-bucket a nurse
	// mid-session as the clock crosses a boundary while someone is reading the rail.
	const [now] = useState<DateTime>(() => DateTime.now());

	const {
		searchQuery,
		selectedRoles,
		selectedJobNames,
		selectedSections,
		selectedStatuses,
		selectedAgencies,
		selectedUnions,
		selectedLastLogin,
		selectedEmail,
		selectedPhone,
		selectedAutoApprove,
		selectedIntegrators,
		selectedHolidayGroups,
		selectedHolidayDayParts,
		activeStatFilter,
		showOnlyDecisions,
	} = urlState;

	// Only jobs the department still has enabled can filter — a URL naming a retired job must not
	// strand the roster at zero rows with no checkbox to clear.
	const selectedEnabledJobNames = useMemo(() => selectedJobNames.filter(isSelectableJobName), [selectedJobNames, isSelectableJobName]);
	// Same for a removed holiday group. Until the groups load, nothing is dropped, so a link's filter holds.
	// With no groups set up there is no holiday section to clear a facet from, and nothing for it to match: both drop.
	const selectedKnownHolidayGroups = useMemo(() => {
		if (holidayNotSetUp) return new Set<string>();
		if (holidayGroups === undefined) return selectedHolidayGroups;
		const known = new Set([HOLIDAY_NONE, ...holidayGroups.map((group) => group.id)]);
		return new Set([...selectedHolidayGroups].filter((groupId) => known.has(groupId)));
	}, [holidayNotSetUp, holidayGroups, selectedHolidayGroups]);
	const selectedKnownHolidayDayParts = useMemo(
		() => (holidayNotSetUp ? new Set<string>() : selectedHolidayDayParts),
		[holidayNotSetUp, selectedHolidayDayParts],
	);
	const holidayFacetActive = selectedKnownHolidayGroups.size > 0 || selectedKnownHolidayDayParts.size > 0;

	/**
	 * Every filter change goes through here.
	 *
	 * `replace` matters: a facet click is a navigation step the Back button should undo, but typing
	 * in the search box is not — one history entry per keystroke would make Back useless.
	 */
	const updateFilters = useCallback(
		(changes: Partial<TeamUrlState>, options?: { replace?: boolean }) => {
			setSearchParams((current) => writeTeamUrlState(current, { ...parseTeamUrlState(current), ...changes }), { replace: options?.replace ?? false });
		},
		[setSearchParams],
	);

	const { data: openDecisions, isPending: decisionsPending } = useOpenStatusDecisions(departmentId);
	const decisionsByNurseId = useMemo<Map<number, OpenStatusDecision>>(() => {
		const map = new Map<number, OpenStatusDecision>();
		for (const decision of openDecisions ?? []) {
			map.set(decision.nurseId, decision);
		}
		return map;
	}, [openDecisions]);
	// DEFAULT: while `useOpenStatusDecisions` is loading or after a failed fetch, `openDecisions` is
	// undefined and the count is 0, so the optional "Needs decision" affordance simply doesn't
	// render. This is a deliberate fail-safe — without the data we can't know of any decisions, and
	// hiding the optional filter is safer than surfacing a wrong count or a broken affordance.
	const decisionsCount = openDecisions?.length ?? 0;

	/**
	 * Decisions mode is filtering against a set that hasn't arrived yet.
	 *
	 * `showOnlyDecisions` resolves every row against `decisionNurseIds`, which is empty until the
	 * fetch lands — so the roster is legitimately zero rows for the length of that request. Without
	 * this the page answers the Employee Status Review notification's own deep link with "No matching
	 * staff · try adjusting or clearing your filters", which is both wrong and the opposite of what
	 * the user should do. Skeleton rows say "still loading" instead.
	 */
	const decisionsRosterPending = showOnlyDecisions && decisionsPending;

	const onSearchChange = useCallback((value: string) => updateFilters({ searchQuery: value }, { replace: true }), [updateFilters]);
	const onRolesChange = useCallback((roles: NurseFullRole[]) => updateFilters({ selectedRoles: roles }), [updateFilters]);
	const onJobsChange = useCallback(
		(jobNames: string[]) => updateFilters({ selectedJobNames: jobNames.filter(isSelectableJobName) }),
		[updateFilters, isSelectableJobName],
	);
	const onSectionsChange = useCallback((sections: Set<string>) => updateFilters({ selectedSections: sections }), [updateFilters]);
	const onStatusesChange = useCallback((statuses: Set<string>) => updateFilters({ selectedStatuses: statuses }), [updateFilters]);
	const onAgenciesChange = useCallback((agencies: Set<string>) => updateFilters({ selectedAgencies: agencies }), [updateFilters]);
	const onUnionsChange = useCallback((unions: Set<string>) => updateFilters({ selectedUnions: unions }), [updateFilters]);
	const onLastLoginChange = useCallback((buckets: Set<string>) => updateFilters({ selectedLastLogin: buckets }), [updateFilters]);
	const onShowOnlyDecisionsChange = useCallback((value: boolean) => updateFilters({ showOnlyDecisions: value }), [updateFilters]);
	const onHolidayGroupsChange = useCallback((groups: Set<string>) => updateFilters({ selectedHolidayGroups: groups }), [updateFilters]);
	const onHolidayDayPartsChange = useCallback((dayParts: Set<string>) => updateFilters({ selectedHolidayDayParts: dayParts }), [updateFilters]);
	const clearHolidayFilters = useCallback(() => updateFilters({ selectedHolidayGroups: new Set(), selectedHolidayDayParts: new Set() }), [updateFilters]);

	const cycleStatFilter = useCallback(
		(key: StatFilterKey) => updateFilters({ activeStatFilter: nextStatFilter(activeStatFilter, key) }),
		[activeStatFilter, updateFilters],
	);

	const clearStatFilter = useCallback(() => updateFilters({ activeStatFilter: null }), [updateFilters]);

	const handleViewAndDecide = useCallback(() => {
		// Decisions mode replaces the status/stat filters but NOT the standard ones — agency, union and
		// auto-approve all still intersect the roster, so every one of them is unrestricted explicitly.
		// Otherwise the default "hide agency staff", or a leftover auto-approve selection, would hide
		// employees the banner's count still includes.
		updateFilters({
			searchQuery: "",
			selectedRoles: [],
			selectedJobNames: [],
			selectedSections: new Set(),
			selectedIntegrators: new Set(),
			selectedAgencies: new Set(),
			selectedUnions: new Set(),
			selectedLastLogin: new Set(),
			selectedEmail: new Set(),
			selectedPhone: new Set(),
			selectedAutoApprove: new Set(),
			selectedHolidayGroups: new Set(),
			selectedHolidayDayParts: new Set(),
			activeStatFilter: null,
			showOnlyDecisions: true,
		});
	}, [updateFilters]);

	const clearAllFilters = useCallback(() => {
		setSearchParams((current) => clearTeamUrlState(current), { replace: false });
	}, [setSearchParams]);

	// Stats are computed against the full population so the per-stat nurse-id sets cover every
	// candidate — the sets power the card click-filters, so they must be complete.
	const teamStats = useTeamCardStats(departmentId, roster);
	// Same idea for a schedule segment from the URL: until the server answers there is nothing to filter
	// against, and showing the whole roster first would have it shrink under the user.
	const scheduleSegmentPending = activeStatFilter !== null && activeStatFilter.key !== "neverLoggedIn" && teamStats.isSchedulePending;
	// A holiday facet filters against groups or people that haven't arrived yet: skeletons, not "no matching staff".
	const holidayFilterPending = holidayFacetActive && !holidayError && (holidayGroupsQuery.isLoading || holidayPeople.isLoading);
	// …and against data that failed to load: the table explains that, with a way to clear the facets.
	const holidayFilterError = holidayFacetActive && holidayError;
	const rosterPending = rosterLoading || decisionsRosterPending || scheduleSegmentPending || holidayFilterPending;

	const activeStatFilterIdSet = useMemo<Set<number> | null>(() => {
		if (!activeStatFilter) return null;
		switch (activeStatFilter.key) {
			// A segment whose card is not rendered must not filter at all: a `?segment=…` link would otherwise
			// reject rows with no tile to clear it. The schedule cards aren't rendered until the server answers,
			// nor after it fails, and the self-scheduling card only exists when the department has a period.
			case "workedLastMonth":
				return teamStats.schedule?.workedLastMonthNurseIds ?? null;
			case "neverLoggedIn":
				return teamStats.neverLoggedInNurseIds;
			case "recurringShifts":
				return teamStats.schedule?.recurringShiftsNurseIds ?? null;
			case "selfScheduling":
				return teamStats.schedule?.selfSchedulingSubmittedNurseIds ?? null;
		}
	}, [activeStatFilter, teamStats]);

	// The retired-job narrowing is the one place the counted state differs from the parsed URL: a
	// selection that can't filter must not show on the badge either. Same rule for the segment — hence
	// `statFilterApplies`, which is false when the department renders no card for it.
	const activeFilterCount = useMemo(
		() =>
			countActiveTeamFilters(
				{
					...urlState,
					selectedJobNames: selectedEnabledJobNames,
					selectedHolidayGroups: selectedKnownHolidayGroups,
					selectedHolidayDayParts: selectedKnownHolidayDayParts,
				},
				{ autoApproveEnabled, statFilterApplies: activeStatFilterIdSet !== null },
			),
		[urlState, selectedEnabledJobNames, selectedKnownHolidayGroups, selectedKnownHolidayDayParts, autoApproveEnabled, activeStatFilterIdSet],
	);

	const hasFiltersApplied = activeFilterCount > 0;

	// The flag gates whether the facet filters at all: while it's off (or still loading) a URL
	// selection stays in the address bar but is not applied, so there are no hidden rows.
	const effectiveAutoApprove = autoApproveEnabled ? selectedAutoApprove : EMPTY_SELECTION;

	// Single source of truth for filtering: the table rows AND the sidebar facet counts run
	// through the same predicates (teamFacetFilters.ts).
	const filterState = useMemo<TeamFilterState>(
		() => ({
			searchQuery,
			selectedRoles,
			selectedJobNames: selectedEnabledJobNames,
			selectedSections,
			selectedStatuses,
			selectedIntegrators,
			selectedAgencies,
			selectedUnions,
			selectedLastLogin,
			selectedEmail,
			selectedPhone,
			selectedAutoApprove: effectiveAutoApprove,
			selectedHolidayGroups: selectedKnownHolidayGroups,
			selectedHolidayDayParts: selectedKnownHolidayDayParts,
			showOnlyDecisions,
		}),
		[
			searchQuery,
			selectedRoles,
			selectedEnabledJobNames,
			selectedSections,
			selectedStatuses,
			selectedIntegrators,
			selectedAgencies,
			selectedUnions,
			selectedLastLogin,
			selectedEmail,
			selectedPhone,
			effectiveAutoApprove,
			selectedKnownHolidayGroups,
			selectedKnownHolidayDayParts,
			showOnlyDecisions,
		],
	);

	const filterContext = useMemo<TeamFilterContext>(() => {
		const statFilterPredicate =
			activeStatFilter && activeStatFilterIdSet
				? (nurse: TeamNurse) => (activeStatFilter.state === "matching" ? activeStatFilterIdSet.has(nurse.id) : !activeStatFilterIdSet.has(nurse.id))
				: null;
		return {
			departmentId,
			enabledJobNames,
			decisionNurseIds: new Set(decisionsByNurseId.keys()),
			statFilterPredicate,
			now,
			// Null while loading or after a failure — a narrowed holiday facet then matches nobody, and the table
			// shows skeletons or the holiday load error instead (`rosterPending`, `holiday.filterError`).
			holidayByNurseId,
		};
	}, [activeStatFilter, activeStatFilterIdSet, departmentId, enabledJobNames, decisionsByNurseId, now, holidayByNurseId]);

	const nurses = useMemo<TeamNurse[]>(() => filterTeamNurses(roster, filterState, filterContext), [roster, filterState, filterContext]);

	const holidayLoading = holidayGroupsQuery.isLoading || holidayPeople.isLoading;
	const holiday = useMemo<TeamHolidayData>(
		() => ({
			groups: holidayGroups,
			notSetUp: holidayNotSetUp,
			byNurseId: filterContext.holidayByNurseId,
			isLoading: holidayLoading,
			isError: holidayError,
			selectedGroups: selectedKnownHolidayGroups,
			selectedDayParts: selectedKnownHolidayDayParts,
			onGroupsChange: onHolidayGroupsChange,
			onDayPartsChange: onHolidayDayPartsChange,
			filterError: holidayFilterError,
			clearFilters: clearHolidayFilters,
		}),
		[
			holidayGroups,
			holidayNotSetUp,
			filterContext.holidayByNurseId,
			holidayLoading,
			holidayError,
			selectedKnownHolidayGroups,
			selectedKnownHolidayDayParts,
			onHolidayGroupsChange,
			onHolidayDayPartsChange,
			holidayFilterError,
			clearHolidayFilters,
		],
	);

	const facetCounts = useMemo(() => computeTeamFacetCounts(roster, filterState, filterContext), [roster, filterState, filterContext]);

	// Mobile roster: the faceted sidebar is web-only, so mobile must never inherit a filter it has no
	// control to see or clear. Filter state now starts at the default on every visit, so the only
	// things left to drop are the web-only stat-card filter, the flag-gated auto-approve facet and the holiday facets.
	const nursesIgnoringStatFilter = useMemo<TeamNurse[]>(() => {
		const statsContext: TeamFilterContext = { ...filterContext, statFilterPredicate: null };
		// A Group balance link sets the holiday facets, and mobile has no rail to clear them from.
		const mobileState: TeamFilterState = {
			...filterState,
			selectedAutoApprove: EMPTY_SELECTION,
			selectedHolidayGroups: EMPTY_SELECTION,
			selectedHolidayDayParts: EMPTY_SELECTION,
		};
		return filterTeamNurses(roster, mobileState, statsContext);
	}, [roster, filterState, filterContext]);

	return {
		searchQuery,
		selectedRoles,
		selectedJobNames: selectedEnabledJobNames,
		selectedSections,
		selectedStatuses,
		selectedIntegrators,
		selectedAgencies,
		selectedUnions,
		selectedLastLogin,
		selectedEmail,
		selectedPhone,
		selectedAutoApprove,
		showOnlyDecisions,
		activeStatFilter,

		onSearchChange,
		onRolesChange,
		onJobsChange,
		onSectionsChange,
		onStatusesChange,
		onAgenciesChange,
		onUnionsChange,
		onLastLoginChange,
		onShowOnlyDecisionsChange,
		cycleStatFilter,
		clearStatFilter,
		clearAllFilters,
		handleViewAndDecide,

		holiday,

		filterState,
		nurses,
		nursesIgnoringStatFilter,
		facetCounts,
		activeFilterCount,
		hasFiltersApplied,
		totalNurseCount: roster.length,
		rosterLoading,
		rosterError,

		teamStats,
		decisionsByNurseId,
		decisionsCount,
		rosterPending,
	};
}
